import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Mic, Layers, ShieldCheck, Zap, MessageSquare, ArrowRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Btn } from "@/components/kit";

const features = [
  { icon: Layers, pt: "Cérebro em 5 camadas", en: "5-layer Brain", dpt: "Empresa, Departamento, Equipe, Funcionário, Cliente e Projeto — cada resposta cruza as camadas respeitando permissão.", den: "Company, Department, Team, Employee, Client and Project — every answer crosses layers respecting permission." },
  { icon: Mic, pt: "Multimodal pelo Zap", en: "Multimodal via WhatsApp", dpt: "Voz, texto, PDF, imagem e planilha entram por adaptadores e convergem num único pipeline lógico.", den: "Voice, text, PDF, image and spreadsheet enter via adapters and converge into one logical pipeline." },
  { icon: MessageSquare, pt: "Memória conversacional", en: "Conversational memory", dpt: "Perguntas encadeadas mantêm o contexto. Não é um bot de FAQ.", den: "Chained questions keep context. Not a FAQ bot." },
  { icon: Zap, pt: "IA proativa", en: "Proactive AI", dpt: "Sugere follow-ups, lembra prazos e age — sempre com confirmação.", den: "Suggests follow-ups, remembers deadlines and acts — always with confirmation." },
  { icon: ShieldCheck, pt: "Anti-alucinação", en: "Anti-hallucination", dpt: "Sem registro? Responde 'sem registro'. Nunca inventa nome, valor ou data.", den: "No record? It says 'no record'. Never invents a name, value or date." },
  { icon: Brain, pt: "Autoria e versão", en: "Authorship & versioning", dpt: "Todo dado tem autor, timestamp e versão. A v2 é arquivada quando a v3 chega.", den: "Every datum has author, timestamp and version. v2 is archived when v3 arrives." },
];

export default function Landing() {
  const { t, lang } = useI18n();
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <nav className="sticky top-0 z-50 border-b border-zinc-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <div className="flex items-center">
            <img src="/asiisa-logo.png" alt="ASIISA" className="h-6 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Btn variant="ghost" data-testid="nav-login">{t("cta_login")}</Btn></Link>
            <Link to="/login"><Btn data-testid="nav-demo">{t("cta_demo")}</Btn></Link>
          </div>
        </div>
      </nav>

      <header className="relative overflow-hidden">
        <div className="relative mx-auto max-w-4xl px-5 py-24 text-center md:py-32">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-indigo-600">{t("tagline")}</p>
            <h1 className="mx-auto max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t("hero_title")}
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base text-zinc-500 sm:text-lg">{t("hero_sub")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/login"><Btn className="px-6 py-3 text-base" data-testid="hero-demo">{t("cta_demo")} <ArrowRight size={18} /></Btn></Link>
              <Link to="/login"><Btn variant="outline" className="px-6 py-3 text-base">{t("cta_login")}</Btn></Link>
            </div>
            <p className="mt-5 text-xs text-zinc-400">Demo: fernando@radar.demo · radar123</p>
          </motion.div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-5 py-20">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          {lang === "pt" ? "Não é um CRM. É memória operacional." : "Not a CRM. It's operational memory."}
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><f.icon size={20} /></span>
              <h3 className="mt-4 font-display text-lg font-semibold">{lang === "pt" ? f.pt : f.en}</h3>
              <p className="mt-2 text-sm text-zinc-500">{lang === "pt" ? f.dpt : f.den}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-4xl px-5 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600">{lang === "pt" ? "Cena real" : "Real scene"}</p>
          <blockquote className="mt-4 font-display text-xl leading-relaxed text-zinc-900 sm:text-2xl">
            "{lang === "pt"
              ? "Acabei de sair do TJ Tocantins, falei com o Dr. Souza, adorou nossa proposta de R$ 380 mil, pediu 15 dias pra decidir com o comitê."
              : "Just left TJ Tocantins, spoke with Dr. Souza, he loved our R$ 380k proposal, asked for 15 days to decide with the committee."}"
          </blockquote>
          <p className="mt-4 text-sm text-zinc-500">
            {lang === "pt"
              ? "→ A Isa transcreve, identifica o autor pelo WhatsApp, extrai conta, contato, valor, sentimento e próximo passo, versiona o anexo e responde no zap."
              : "→ Isa transcribes, identifies the author by WhatsApp, extracts account, contact, value, sentiment and next step, versions the attachment and replies on WhatsApp."}
          </p>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-5 py-12 text-sm text-zinc-500">
        <div className="flex items-center"><img src="/asiisa-logo.png" alt="ASIISA" className="h-5 w-auto" /></div>
        <p className="mt-2">© 2026 ASIISA · {t("tagline")}</p>
      </footer>
    </div>
  );
}
